import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableItems(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
}

function isTextEntry(element) {
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
}

function Dropdown({ anchorEl, children, className, handleClose = () => {}, open, placement, style }) {
  const contentRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => {
      const items = getFocusableItems(contentRef.current);
      (items[0] || contentRef.current)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleClose(event);
      anchorEl?.focus?.();
      return;
    }

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isTextEntry(document.activeElement)) {
      const items = getFocusableItems(contentRef.current);
      if (!items.length) return;
      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + delta + items.length) % items.length;
      items[nextIndex].focus();
    }
  }

  return (
    <div className="dropdown">
      <div>
        <Popper anchorEl={anchorEl} className={clsx(className, 'dropdown__lib')} open={open} placement={placement} role={undefined} style={style} transition>
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'center top' }}>
              <Paper onKeyDown={onKeyDown}>
                <ClickAwayListener onClickAway={handleClose}>
                  <div ref={contentRef} tabIndex={-1}>
                    {children}
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  anchorEl: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  className: PropTypes.string,
  handleClose: PropTypes.func,
  open: PropTypes.bool,
  placement: PropTypes.string,
  style: PropTypes.object,
};

export default Dropdown;
